import { useEffect, useState } from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { ChevronDown, Menu, X } from "lucide-react"
import PublicNavDropdown from "@/components/PublicNavDropdown"
import {
  LANGUAGE_NAMES,
  localeSwitchTarget,
  otherLocale,
  rememberLanguageChoice,
  useLocalePath,
} from "@/lib/locale-path"
import { cn } from "@/lib/utils"
import type { ConditionMenuGroup, ConditionMenuItem, PageProps } from "@/types/inertia"

export default function PublicNav() {
  const { url, props: { current_locale, translations, brand_urls, hidden_paths, locale_slugs } } =
    usePage<PageProps>()
  // Empty until the shared prop is there (SSR smoke renders with bare props).
  const sanctuary_locations = usePage<PageProps>().props.sanctuary_locations ?? []
  const t = translations.nav
  const lp = useLocalePath()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [locationsOpen, setLocationsOpen] = useState(false)

  // Pages switched off in the admin drop out of the nav — filtered on the
  // raw English path, before lp() localizes it.
  const visible = (path: string) => !hidden_paths.includes(path)
  const showLocations = visible("/locations")

  // The one menu: the locations (Site.menu_items), under "Our locations".
  // Rent a Room stays a plain link — its page opens with a card per location,
  // so a second menu listing the same places only read as repetition.
  const locationItems: ConditionMenuItem[] = sanctuary_locations.map(({ label, path, page }) => ({ label, path, page }))
  const locationsMenu: ConditionMenuGroup[] =
    locationItems.length > 0 ? [{ label: "", flyout: false, items: locationItems }] : []

  const leadingLinks = [
    { label: t.therapy_with_gisela, path: "/therapy-with-gisela" },
    { label: t.rent_a_room,         path: "/rent-a-room" },
  ]
    .filter(({ path }) => visible(path))
    .map(({ label, path }) => ({ label, href: lp(path) }))
  const trailingLinks = [
    // Gallery follows the locations menu: a visitor who hasn't picked a
    // location yet browses the rooms first, then chooses.
    { label: t.gallery, path: "/gallery" },
    { label: t.about,   path: "/about" },
    { label: t.contact, path: "/contact" },
  ]
    .filter(({ path }) => visible(path))
    .map(({ label, path }) => ({ label, href: lp(path) }))

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40)
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  useEffect(() => {
    setMobileOpen(false)
    setLocationsOpen(false)
  }, [url])

  const switchLocale = (locale: string) => {
    rememberLanguageChoice(locale)
    const { path, query } = localeSwitchTarget(locale, window.location.pathname, locale_slugs)
    router.get(path, query, {
      preserveScroll: true,
      preserveState: false,
    })
  }

  const linkClass = cn("text-sm transition-colors hover:opacity-70", scrolled ? "text-ink-body" : "text-white")
  const mobileLinkClass = "rounded-md px-2 py-2.5 text-sm text-ink-body no-underline transition-colors hover:bg-surface"

  return (
    <nav
      className={cn(
        "fixed top-(--admin-bar-h) left-0 right-0 z-50 transition-all duration-300",
        scrolled
          ? "bg-cream/90 backdrop-blur-sm border-b border-hairline shadow-sm"
          : "bg-transparent"
      )}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-3">
          {/* The full lockup — mark plus "Sanctuary Therapy Centre" — the
              same artwork Gisela's site uses to name the centre. It replaces
              the mark-plus-wordmark pair that hid its wordmark below sm,
              which left the mobile header unnamed. Two cuts, because this
              nav sits on a photo until it scrolls: the light one over the
              hero, the blue one over cream. Both are loaded and one is
              hidden, so the swap on scroll never flashes an unloaded image.
              The artwork runs flush to all four edges of the PNG, so it must
              NOT be rounded or cropped — that clips the outer brushstroke. */}
          <Link href={lp("/")} className="flex min-w-0 items-center">
            <img
              src="/logos/sanctuary-lockup-light.png"
              alt={t.site_name}
              className={cn(
                "h-auto w-[min(7.5rem,31vw)] object-contain object-left sm:h-9 sm:w-auto md:h-10",
                scrolled && "hidden"
              )}
            />
            <img
              src="/logos/sanctuary-lockup.png"
              alt={t.site_name}
              className={cn(
                "h-auto w-[min(7.5rem,31vw)] object-contain object-left sm:h-9 sm:w-auto md:h-10",
                !scrolled && "hidden"
              )}
            />
          </Link>

          {/* Nav links: Therapy with Gisela · Rent a Room · Our locations ▾ ·
              About · Contact. The menu is the same crawlable dropdown Gisela's
              nav uses, in this nav's colours. */}
          <div className="hidden items-center gap-6 md:flex">
            {leadingLinks.map(({ label, href }) => (
              <a key={label} href={href} className={linkClass}>
                {label}
              </a>
            ))}
            {showLocations && (
              <PublicNavDropdown
                label={t.locations}
                href="/locations"
                menu={locationsMenu}
                browseLabel={t.browse_locations}
                scrolled={scrolled}
              />
            )}
            {trailingLinks.map(({ label, href }) => (
              <a key={label} href={href} className={linkClass}>
                {label}
              </a>
            ))}
          </div>

          {/* Right actions */}
          <div className="flex shrink-0 items-center gap-3">
            {/* Language toggle: one button naming the language it switches
                to, in that language — the person who needs it is the one who
                cannot read the label beside it. */}
            <div className="hidden text-xs md:block">
              <button
                lang={otherLocale(current_locale)}
                onClick={() => switchLocale(otherLocale(current_locale))}
                className={cn(
                  "rounded-full px-2.5 py-1 font-medium transition-colors",
                  scrolled
                    ? "text-ink-muted hover:bg-surface hover:text-ink-body"
                    : "text-white/80 hover:bg-white/15 hover:text-white"
                )}
              >
                {LANGUAGE_NAMES[otherLocale(current_locale)]}
              </button>
            </div>

            {/* Start therapy CTA — the enquiry form lives on Gisela's own
                site's contact page, so this crosses domains via brand_urls. */}
            <a
              href={`${brand_urls.gisela}${lp("/contact")}#enquiry-form`}
              className="rounded-full bg-teal px-3 py-1.5 text-xs font-medium whitespace-nowrap text-white transition-opacity hover:opacity-90 sm:px-4 sm:text-sm"
            >
              {t.start_therapy}
            </a>

            {/* Mobile menu toggle */}
            <button
              type="button"
              onClick={() => setMobileOpen((open) => !open)}
              aria-expanded={mobileOpen}
              aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
              className={cn(
                "inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors md:hidden",
                scrolled ? "text-ink-body hover:bg-surface" : "text-white hover:bg-white/10"
              )}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="border-t border-hairline bg-cream shadow-lg md:hidden">
          <div className="flex flex-col gap-1 px-6 py-4">
            {leadingLinks.map(({ label, href }) => (
              <a key={label} href={href} className={mobileLinkClass}>
                {label}
              </a>
            ))}

            {/* The menu as an expandable section, as on Gisela's nav: the
                label still navigates, the chevron opens the list. */}
            {showLocations && (
              <MobileMenuSection
                label={t.locations}
                href={lp("/locations")}
                items={locationsMenu.flatMap((group) => group.items)}
                open={locationsOpen}
                onToggle={() => setLocationsOpen((open) => !open)}
                browseLabel={t.browse_locations}
                lp={lp}
              />
            )}

            {trailingLinks.map(({ label, href }) => (
              <a key={label} href={href} className={mobileLinkClass}>
                {label}
              </a>
            ))}
          </div>
          <div className="flex items-center justify-between border-t border-hairline px-6 py-3">
            <div className="text-xs">
              <button
                lang={otherLocale(current_locale)}
                onClick={() => switchLocale(otherLocale(current_locale))}
                className="-ml-2.5 rounded-full px-2.5 py-1 font-medium text-ink-muted transition-colors hover:bg-surface hover:text-ink-body"
              >
                {LANGUAGE_NAMES[otherLocale(current_locale)]}
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}

/** One expandable entry in the burger panel: a link that navigates, a chevron
 *  that opens the list under it. */
function MobileMenuSection({
  label,
  href,
  items,
  open,
  onToggle,
  browseLabel,
  lp,
}: {
  label: string
  href: string
  items: ConditionMenuItem[]
  open: boolean
  onToggle: () => void
  browseLabel: string
  lp: (path: string) => string
}) {
  return (
    <>
      <div className="flex items-center gap-1">
        <a
          href={href}
          className="flex-1 rounded-md px-2 py-2.5 text-sm text-ink-body no-underline transition-colors hover:bg-surface"
        >
          {label}
        </a>
        {items.length > 0 && (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-label={browseLabel}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-surface hover:text-ink-body"
          >
            <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
          </button>
        )}
      </div>
      {open && (
        <div className="mb-1 flex flex-col gap-0.5 border-l border-hairline pl-1">
          {items.map((item) => (
            <a
              key={item.path}
              href={lp(item.path)}
              className="rounded-md px-2 py-2 text-sm text-ink-body no-underline transition-colors hover:bg-surface"
            >
              {item.label}
            </a>
          ))}
        </div>
      )}
    </>
  )
}
